import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button, TextField, InputAdornment,
  ToggleButton, ToggleButtonGroup, FormControl, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  getAllCommissionProgress, getCommissionProgressByAgentId, getCommissionProgressByGroupName,
} from '../../api/commissionProgressApi';
import { getAllExtraSlips, getExtraSlipsByAgent, getExtraSlipsByGroupName } from '../../api/extraSlipApi';
import { getAllIncentiveSlips, getIncentiveSlipsByAgentId, getIncentiveSlipsByGroupName } from '../../api/incentiveSlipApi';
import { getAllAgentGroups } from '../../api/userApi';
import { useUser } from '../../context/userContext';
import { handlePriceWithRM, formatCompactRm } from '../../utils/priceUtils';

dayjs.extend(quarterOfYear);

const FONT = "'Poppins', sans-serif";

const TYPE_COLORS = {
  PSC: '#1A8CFF',
  DOC: '#10B981',
  TOC: '#F59E0B',
  MOC: '#8B5CF6',
  Slips: '#F97316',
};

const DATE_PRESETS = [
  { label: 'All Time', start: () => null, end: () => null },
  { label: 'This Month', start: () => dayjs().startOf('month'), end: () => dayjs() },
  { label: 'Last Month', start: () => dayjs().subtract(1, 'month').startOf('month'), end: () => dayjs().subtract(1, 'month').endOf('month') },
  { label: 'This Quarter', start: () => dayjs().startOf('quarter'), end: () => dayjs() },
  { label: 'This Year', start: () => dayjs().startOf('year'), end: () => dayjs() },
];

// ─── Type buckets ───────────────────────────────────────────────────────────
const PSC_TYPES = ['PSC', 'PSC/2'];
const DOC_TYPES = ['DOC', 'DOC/2'];
const TOC_TYPES = ['TOC', 'TOC/2'];
const MOC_TYPES = ['MOC', 'MOC/2'];
const ALL_AGENT_TYPES = [...PSC_TYPES, ...DOC_TYPES, ...TOC_TYPES, ...MOC_TYPES];
const TOTAL_COMM_TYPE = 'Total Commission';

// ─── Sum payments on a progress row whose payment date falls within range ──
const paymentsInRange = (cd, start, end) => {
  if (!cd) return 0;
  const slots = [
    [cd.firstPayment, cd.firstPaymentOn],
    [cd.secondPayment, cd.secondPaymentOn],
    [cd.repayment, cd.repaymentOn],
    // Field names are repayment2On..repayment5On (DTO Repayment2On..Repayment5On).
    // These read repaymentOn2..repaymentOn5, which are undefined, so the `!on` guard
    // below silently skipped every payment keyed into repayment slots 2-5 — the report
    // under-stated payouts with no error. (bug 2026-08-25)
    [cd.repayment2, cd.repayment2On],
    [cd.repayment3, cd.repayment3On],
    [cd.repayment4, cd.repayment4On],
    [cd.repayment5, cd.repayment5On],
  ];
  let sum = 0;
  for (const [amt, on] of slots) {
    if (!amt || !on) continue;
    const d = dayjs(on);
    if (start && d.isBefore(start, 'day')) continue;
    if (end && d.isAfter(end, 'day')) continue;
    sum += amt;
  }
  return sum;
};

// Payout statement row. The report reads as an income statement — claimed at the top,
// each commission type deducted, nett at the bottom — so it is laid out as one rather
// than as a grid of cards you have to mentally add up. (user 2026-08-25)
// `pct` is each line's share of Claimed, so the statement answers "how much of what we
// claimed went where" without the reader doing the division. (user 2026-08-25)
const Row = ({ sign, label, sub, value, pct, kind = 'line', color }) => {
  const isTotal = kind === 'total';
  const isSub = kind === 'subtotal';
  const isOpen = kind === 'open';
  const isChild = kind === 'child';   // indented "of which" line under a subtotal
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '28px 1fr 58px auto',
      alignItems: 'baseline',
      gap: 1,
      px: { xs: 1.5, md: 2 }, py: isTotal ? 1.4 : isChild ? 0.55 : 0.9,
      pl: isChild ? { xs: 4, md: 5.5 } : { xs: 1.5, md: 2 },
      borderTop: isSub || isTotal ? '1px solid #CBD5E1' : isChild ? 'none' : '1px solid #F1F5F9',
      borderBottom: isTotal ? '2px solid #334155' : 'none',
      background: isTotal ? '#F8FAFC' : isChild ? '#FBFCFD' : 'transparent',
    }}>
      <Box sx={{
        fontFamily: FONT, fontSize: '0.95rem', fontWeight: 700, textAlign: 'center',
        color: sign === '−' ? '#DC2626' : sign === '+' ? '#059669' : '#94A3B8',
      }}>{sign}</Box>
      <Box>
        <Typography sx={{
          fontFamily: FONT, fontSize: isTotal ? '0.95rem' : isChild ? '0.8rem' : '0.875rem',
          fontWeight: isTotal || isSub || isOpen ? 700 : isChild ? 400 : 500,
          color: color || (isTotal ? '#0F172A' : isChild ? '#64748B' : '#334155'),
        }}>{label}</Typography>
        {sub && <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', color: '#94A3B8' }}>{sub}</Typography>}
      </Box>
      <Typography sx={{
        fontFamily: FONT, fontVariantNumeric: 'tabular-nums', textAlign: 'right',
        whiteSpace: 'nowrap', fontSize: isChild ? '0.72rem' : '0.76rem',
        fontWeight: isTotal ? 600 : 400, color: '#94A3B8',
      }}>{pct || ''}</Typography>
      <Typography sx={{
        fontFamily: FONT, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
        fontSize: isTotal ? '1.15rem' : isChild ? '0.85rem' : '0.95rem',
        fontWeight: isTotal || isSub || isOpen ? 700 : isChild ? 400 : 500,
        color: color || (isChild ? '#64748B' : sign === '−' ? '#DC2626' : '#0F172A'),
      }}>{value}</Typography>
    </Box>
  );
};

const StatCard = ({ label, value, sub, borderColor }) => (
  <Card sx={{ borderLeft: `4px solid ${borderColor}`, borderRadius: '14px' }}>
    <CardContent sx={{ p: '14px !important', '&:last-child': { pb: '14px !important' } }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', mt: 0.5, fontFamily: FONT }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.25, fontFamily: FONT }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

export default function PayoutReport() {
  const { user } = useUser();
  const navigate = useNavigate();

  // Role-driven scope (matches Sales / Commission Reports).
  const roles = user?.roles || [];
  const isAccountantOrSuper = roles.includes('Accountant') || roles.includes('Super Admin');
  const hasAny = (list) => roles.some(r => list.includes(r));
  const availableScopes = useMemo(() => {
    // Widest scope FIRST (user 2026-08-25). This report exists to read the whole company,
    // so All leads and the narrower views sit behind it — the reverse of the agent-facing
    // reports, where Personal is the natural starting point.
    const t = [];
    if (hasAny(['Accountant', 'Super Admin']))                                            t.push({ key: 'all',      label: 'All' });
    if (hasAny(['Leader', 'Accountant', 'Super Admin']))                                  t.push({ key: 'group',    label: 'Group' });
    if (hasAny(['Sales Agent', 'In-house Agent', 'Leader', 'Accountant', 'Super Admin'])) t.push({ key: 'personal', label: 'Personal' });
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Open on the widest scope the viewer is entitled to — for an Accountant or Super Admin
  // that is All, which is what this report is for. Falls back to whatever they do have,
  // so a Leader still lands on Personal. (user 2026-08-25)
  const widestScope = (list) => (list.find(s => s.key === 'all') || list.find(s => s.key === 'group') || list[0])?.key || '';
  const [scopeTab, setScopeTab] = useState(widestScope(availableScopes));
  useEffect(() => {
    if (!scopeTab && availableScopes.length > 0) setScopeTab(widestScope(availableScopes));
  }, [availableScopes, scopeTab]);

  const [groups, setGroups] = useState([]);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  useEffect(() => {
    if (!isAccountantOrSuper) return;
    let cancelled = false;
    getAllAgentGroups().then(r => {
      if (cancelled) return;
      const list = r.data || [];
      setGroups(list);
      if (!selectedGroupName && list.length > 0) setSelectedGroupName(list[0].groupName || list[0].name || '');
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountantOrSuper]);

  const groupNameForQuery = isAccountantOrSuper ? selectedGroupName : (user?.groupName || '');

  const [progress, setProgress] = useState([]);
  const [extraSlips, setExtraSlips] = useState([]);
  const [incentiveSlips, setIncentiveSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activePreset, setActivePreset] = useState('All Time');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [agentSearch, setAgentSearch] = useState('');

  const fetchData = useCallback(async () => {
    if (!scopeTab) { setLoading(false); return; }
    setLoading(true);
    try {
      let progRes, extraRes, incRes;
      if (scopeTab === 'all') {
        [progRes, extraRes, incRes] = await Promise.all([
          getAllCommissionProgress(),
          getAllExtraSlips(),
          getAllIncentiveSlips(),
        ]);
      } else if (scopeTab === 'group') {
        if (!groupNameForQuery) {
          setProgress([]); setExtraSlips([]); setIncentiveSlips([]);
          setLoading(false); return;
        }
        [progRes, extraRes, incRes] = await Promise.all([
          getCommissionProgressByGroupName(groupNameForQuery),
          getExtraSlipsByGroupName(groupNameForQuery),
          getIncentiveSlipsByGroupName(groupNameForQuery),
        ]);
      } else {
        [progRes, extraRes, incRes] = await Promise.all([
          getCommissionProgressByAgentId(user?.userId),
          getExtraSlipsByAgent(user?.userId),
          getIncentiveSlipsByAgentId(user?.userId),
        ]);
      }
      setProgress(progRes?.data || []);
      setExtraSlips(Array.isArray(extraRes) ? extraRes : (extraRes?.data || []));
      setIncentiveSlips(incRes?.data || []);
    } catch (err) {
      console.error('Error loading payout report:', err);
      setProgress([]); setExtraSlips([]); setIncentiveSlips([]);
    } finally {
      setLoading(false);
    }
  }, [scopeTab, groupNameForQuery, user?.userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePreset = (p) => {
    setActivePreset(p.label);
    setStartDate(p.start());
    setEndDate(p.end());
  };

  // ─── In: Astern claim payments within range ──────────────────────────────
  const totalIn = useMemo(() =>
    progress
      .filter(r => r.commissionProgressDto?.commPayType === TOTAL_COMM_TYPE)
      .reduce((s, r) => s + paymentsInRange(r.commissionProgressDto, startDate, endDate), 0),
    [progress, startDate, endDate]
  );

  // ─── Out: per-type aggregates ────────────────────────────────────────────
  const sumByTypes = useCallback((types) =>
    progress
      .filter(r => types.includes(r.commissionProgressDto?.commPayType))
      .reduce((s, r) => s + paymentsInRange(r.commissionProgressDto, startDate, endDate), 0),
    [progress, startDate, endDate]
  );

  const pscTotal = useMemo(() => sumByTypes(PSC_TYPES), [sumByTypes]);
  const docTotal = useMemo(() => sumByTypes(DOC_TYPES), [sumByTypes]);
  const tocTotal = useMemo(() => sumByTypes(TOC_TYPES), [sumByTypes]);
  const mocTotal = useMemo(() => sumByTypes(MOC_TYPES), [sumByTypes]);

  // ─── Slips: approved Incentive + Extra in date range ─────────────────────
  const slipDateInRange = (date) => {
    if (!date) return false;
    const d = dayjs(date);
    if (startDate && d.isBefore(startDate, 'day')) return false;
    if (endDate && d.isAfter(endDate, 'day')) return false;
    return true;
  };

  const slipsTotal = useMemo(() => {
    const inc = incentiveSlips
      .filter(s => s.status === 'Approved' && slipDateInRange(s.approvedOn ?? s.dateIssued))
      .reduce((sum, s) => sum + (s.totalPayout || 0), 0);
    const extra = extraSlips
      .filter(s => s.status === 'Approved' && slipDateInRange(s.approver2Date ?? s.dateIssued))
      .reduce((sum, s) => sum + (s.amount || 0), 0);
    return inc + extra;
  }, [incentiveSlips, extraSlips, startDate, endDate]);

  const totalOut = pscTotal + docTotal + tocTotal + mocTotal + slipsTotal;
  // Commission paid to agents. Slips (Incentive + Extra) are real money out but are NOT
  // part of the Astern commission claim, so they are reported separately and never netted
  // against it — otherwise Nett would understate the margin. (user 2026-08-25)
  const paidOut = pscTotal + docTotal + tocTotal + mocTotal;
  const netRetained = totalIn - paidOut;

  // The Webbey Commission row is the system's OWN record of the same margin, so it is a
  // free reconciliation check on Nett. The two agree across all time (~0.2% apart) but
  // NOT month to month, because a sale's three legs settle on different dates: the Astern
  // claim may land in July, the PSC in August and the Webbey share later again. A small
  // drift is that timing, not an error — a large or growing one means claims and payouts
  // have genuinely drifted apart. (user 2026-08-25: "webbey commission compare aso")
  const webbeyActual = useMemo(() =>
    progress
      .filter(r => r.commissionProgressDto?.commPayType === 'Webbey Commission')
      .reduce((s, r) => s + paymentsInRange(r.commissionProgressDto, startDate, endDate), 0),
    [progress, startDate, endDate]
  );
  const webbeyDrift = netRetained - webbeyActual;

  // ─── Tan Jin Sheng's earnings (for "what if TJS were kept by company") ──
  // Hardcoded id; if TJS's userId differs in prod, just bump the constant.
  const TJS_USER_ID = 30;

  const tjsByTypes = useCallback((types) =>
    progress
      .filter(r => r.commissionProgressDto?.paymentTo === TJS_USER_ID
                && types.includes(r.commissionProgressDto?.commPayType))
      .reduce((s, r) => s + paymentsInRange(r.commissionProgressDto, startDate, endDate), 0),
    [progress, startDate, endDate]
  );
  const tjsPsc = useMemo(() => tjsByTypes(PSC_TYPES), [tjsByTypes]);
  const tjsDoc = useMemo(() => tjsByTypes(DOC_TYPES), [tjsByTypes]);
  const tjsToc = useMemo(() => tjsByTypes(TOC_TYPES), [tjsByTypes]);
  const tjsMoc = useMemo(() => tjsByTypes(MOC_TYPES), [tjsByTypes]);
  const tjsOverrides = tjsPsc + tjsDoc + tjsToc + tjsMoc;

  const tjsSlips = useMemo(() => {
    const inc = incentiveSlips
      .filter(s => s.agentId === TJS_USER_ID && s.status === 'Approved' && slipDateInRange(s.approvedOn ?? s.dateIssued))
      .reduce((sum, s) => sum + (s.totalPayout || 0), 0);
    const extra = extraSlips
      .filter(s => s.agentId === TJS_USER_ID && s.status === 'Approved' && slipDateInRange(s.approver2Date ?? s.dateIssued))
      .reduce((sum, s) => sum + (s.amount || 0), 0);
    return inc + extra;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incentiveSlips, extraSlips, startDate, endDate]);
  const tjsTotal = tjsOverrides + tjsSlips;

  // ─── Profit ratios + TJS-combined views ─────────────────────────────────
  const pctOfIn = (v) => totalIn > 0 ? ((v / totalIn) * 100).toFixed(1) + '%' : '—';
  const profitMarginPct = totalIn > 0 ? ((netRetained / totalIn) * 100).toFixed(1) + '%' : '—';
  const commissionRatioPct = totalIn > 0 ? ((totalOut / totalIn) * 100).toFixed(1) + '%' : '—';
  // The "what if TJS earnings stayed with the company" figures were removed with their
  // cards — TJS is now a real line inside the statement, so the hypothetical is redundant.
  // (user 2026-08-25)

  // ─── Monthly trend for In vs Out ─────────────────────────────────────────
  const monthlyTrend = useMemo(() => {
    const map = {};
    const bumpProgress = (types, key) => {
      progress.filter(r => types.includes(r.commissionProgressDto?.commPayType)).forEach(r => {
        const cd = r.commissionProgressDto;
        if (!cd) return;
        const slots = [
          [cd.firstPayment, cd.firstPaymentOn],
          [cd.secondPayment, cd.secondPaymentOn],
          [cd.repayment, cd.repaymentOn],
          // Same field-name fix as paymentsInRange above — this second copy of the slot
          // list had the identical typo. (bug 2026-08-25)
          [cd.repayment2, cd.repayment2On],
          [cd.repayment3, cd.repayment3On],
          [cd.repayment4, cd.repayment4On],
          [cd.repayment5, cd.repayment5On],
        ];
        for (const [amt, on] of slots) {
          if (!amt || !on) continue;
          const d = dayjs(on);
          if (startDate && d.isBefore(startDate, 'day')) continue;
          if (endDate && d.isAfter(endDate, 'day')) continue;
          const m = d.format('MMM YYYY');
          if (!map[m]) map[m] = { name: m, In: 0, Out: 0 };
          map[m][key] += amt;
        }
      });
    };
    bumpProgress([TOTAL_COMM_TYPE], 'In');
    bumpProgress(ALL_AGENT_TYPES, 'Out');

    // Slips → Out
    const bumpSlip = (date, amount) => {
      if (!date || !amount) return;
      const d = dayjs(date);
      if (startDate && d.isBefore(startDate, 'day')) return;
      if (endDate && d.isAfter(endDate, 'day')) return;
      const m = d.format('MMM YYYY');
      if (!map[m]) map[m] = { name: m, In: 0, Out: 0 };
      map[m].Out += amount;
    };
    incentiveSlips.filter(s => s.status === 'Approved').forEach(s => bumpSlip(s.approvedOn ?? s.dateIssued, s.totalPayout));
    extraSlips.filter(s => s.status === 'Approved').forEach(s => bumpSlip(s.approver2Date ?? s.dateIssued, s.amount));

    return Object.values(map).sort((a, b) => dayjs(a.name, 'MMM YYYY').valueOf() - dayjs(b.name, 'MMM YYYY').valueOf());
  }, [progress, incentiveSlips, extraSlips, startDate, endDate]);

  // ─── Agent breakdown (per recipient) ─────────────────────────────────────
  // Build candidate set: every user who has ever been a paymentTo on an agent-type
  // row, or an agentId on any slip. This way every possible commission earner is
  // visible — period totals show 0 when they earned nothing in range.
  const agentBreakdown = useMemo(() => {
    const map = {};
    const ensure = (id, name) => {
      if (!id) return null;
      if (!map[id]) map[id] = { paymentTo: id, name: name || 'Unknown', psc: 0, doc: 0, toc: 0, moc: 0, slips: 0, total: 0 };
      // Refresh name if the previous entry was 'Unknown' but a better one came in
      else if (name && map[id].name === 'Unknown') map[id].name = name;
      return map[id];
    };

    // Seed: every agent-type progress recipient (any date)
    progress.forEach(r => {
      const cd = r.commissionProgressDto;
      const t = cd?.commPayType;
      if (!t || !ALL_AGENT_TYPES.includes(t)) return;
      ensure(cd.paymentTo, cd.paymentToName);
    });
    // Seed: every slip recipient
    incentiveSlips.forEach(s => ensure(s.agentId, s.agentName));
    extraSlips.forEach(s => ensure(s.agentId, s.agentName));

    // Fill period values
    progress.forEach(r => {
      const cd = r.commissionProgressDto;
      const t = cd?.commPayType;
      if (!t || !ALL_AGENT_TYPES.includes(t)) return;
      const id = cd.paymentTo;
      if (!id) return;
      const paid = paymentsInRange(cd, startDate, endDate);
      if (paid <= 0) return;
      const row = ensure(id, cd.paymentToName);
      if (PSC_TYPES.includes(t)) row.psc += paid;
      else if (DOC_TYPES.includes(t)) row.doc += paid;
      else if (TOC_TYPES.includes(t)) row.toc += paid;
      else if (MOC_TYPES.includes(t)) row.moc += paid;
      row.total += paid;
    });

    incentiveSlips.filter(s => s.status === 'Approved' && slipDateInRange(s.approvedOn ?? s.dateIssued)).forEach(s => {
      const row = ensure(s.agentId, s.agentName);
      if (!row) return;
      row.slips += (s.totalPayout || 0);
      row.total += (s.totalPayout || 0);
    });
    extraSlips.filter(s => s.status === 'Approved' && slipDateInRange(s.approver2Date ?? s.dateIssued)).forEach(s => {
      const row = ensure(s.agentId, s.agentName);
      if (!row) return;
      row.slips += (s.amount || 0);
      row.total += (s.amount || 0);
    });

    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [progress, incentiveSlips, extraSlips, startDate, endDate]);

  // Filter by name search
  const filteredAgents = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    if (!q) return agentBreakdown;
    return agentBreakdown.filter(a => (a.name || '').toLowerCase().includes(q));
  }, [agentBreakdown, agentSearch]);

  // Reset page when filter or date changes
  useEffect(() => { setPage(0); }, [agentSearch, startDate, endDate]);

  if (loading) return <div>Loading...</div>;

  if (availableScopes.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>Payout Report</h2>
        <Typography sx={{ fontFamily: FONT, color: '#64748B', mt: 1 }}>
          Your role does not have access to any Payout Report view.
        </Typography>
      </Box>
    );
  }

  const scopeSubtitle = scopeTab === 'all'
    ? 'Cash in vs cash out across all groups · filtered by payment date'
    : scopeTab === 'group'
    ? `Group payouts — ${groupNameForQuery || 'your group'} · filtered by payment date`
    : 'Your personal payouts · filtered by payment date';

  const datePickerSx = { width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: FONT, fontSize: '0.85rem' } };

  return (
    <div>
      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ fontFamily: FONT, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #10B981, #059669)', boxShadow: 'none', fontSize: '0.78rem', fontWeight: 600 }}>
          Print / Save as PDF
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>Payout Report</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: FONT }}>{scopeSubtitle}</p>
        </div>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DatePicker value={startDate} onChange={v => v && (setStartDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
          <Typography sx={{ color: '#64748B', fontSize: '0.85rem' }}>to</Typography>
          <DatePicker value={endDate} onChange={v => v && (setEndDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
        </Box>
      </Box>

      {/* Scope tabs (Personal / Group / All) */}
      {availableScopes.length > 1 && (
        <Box className="no-print" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ToggleButtonGroup exclusive value={scopeTab} onChange={(_, v) => v && setScopeTab(v)} size="small"
            sx={{ '& .MuiToggleButton-root': { fontFamily: FONT, fontSize: '0.78rem', textTransform: 'none', py: 0.6, px: 1.5 },
                  '& .Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC) !important', color: 'white !important' } }}>
            {availableScopes.map(t => <ToggleButton key={t.key} value={t.key}>{t.label}</ToggleButton>)}
          </ToggleButtonGroup>
          {scopeTab === 'group' && isAccountantOrSuper && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select value={selectedGroupName} onChange={(e) => setSelectedGroupName(e.target.value)}
                displayEmpty sx={{ fontFamily: FONT, fontSize: '0.82rem', borderRadius: '8px' }}>
                {groups.length === 0 && <MenuItem value="" disabled>No groups</MenuItem>}
                {groups.map(g => {
                  const name = g.groupName || g.name || '';
                  return <MenuItem key={name} value={name} sx={{ fontFamily: FONT, fontSize: '0.82rem' }}>{name}</MenuItem>;
                })}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 0.75, mb: 3, flexWrap: 'wrap' }}>
        {DATE_PRESETS.map(p => (
          <Chip key={p.label} label={p.label} size="small" onClick={() => handlePreset(p)}
            sx={{
              fontFamily: FONT, fontSize: '0.75rem',
              fontWeight: activePreset === p.label ? 600 : 400,
              backgroundColor: activePreset === p.label ? '#1A8CFF' : '#F1F5F9',
              color: activePreset === p.label ? '#fff' : '#475569',
              '&:hover': { backgroundColor: activePreset === p.label ? '#1574D4' : '#E2E8F0' },
            }} />
        ))}
      </Box>

      {/* ── Payout statement ─────────────────────────────────────────────────
          Reads top to bottom: what came in from Astern, what went out to each
          commission type, what is left. Slips sit below the rule because they
          are not part of the commission claim and must not reduce Nett. */}
      <Box sx={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Row sign="+" kind="open" label="Claimed" sub="Total Commission received from Astern" value={handlePriceWithRM(totalIn)} pct={totalIn>0?"100%":"—"} />
        <Row sign="−" label="PSC" sub="Personal" value={handlePriceWithRM(pscTotal)} pct={pctOfIn(pscTotal)} />
        <Row sign="−" label="DOC" sub="Downline override" value={handlePriceWithRM(docTotal)} pct={pctOfIn(docTotal)} />
        <Row sign="−" label="TOC" sub="Team override" value={handlePriceWithRM(tocTotal)} pct={pctOfIn(tocTotal)} />
        <Row sign="−" label="MOC" sub="Manager override" value={handlePriceWithRM(mocTotal)} pct={pctOfIn(mocTotal)} />
        <Row sign="" kind="subtotal" label="Paid Out" sub="PSC + DOC + TOC + MOC" value={handlePriceWithRM(paidOut)} pct={pctOfIn(paidOut)} />
        {/* Who that payout actually went to. TJS earns commission like any agent, so his
            share is inside Paid Out — splitting it out is the only way to see how much of
            the payout leaves the company vs stays with the founder. (user 2026-08-25) */}
        <Row sign="" kind="child" label="Tan Jin Sheng" value={handlePriceWithRM(tjsOverrides)} pct={pctOfIn(tjsOverrides)} />
        <Row sign="" kind="child" label="All other agents" value={handlePriceWithRM(paidOut - tjsOverrides)} pct={pctOfIn(paidOut - tjsOverrides)} />
        <Row sign="=" kind="total" label="Nett" sub="Claimed − Paid Out" value={handlePriceWithRM(netRetained)} pct={pctOfIn(netRetained)} />
      </Box>

      {/* Where the money the company keeps actually sits: the recorded Webbey margin,
          and what TJS took, itemised. */}
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: FONT, mb: 1 }}>
        Webbey &amp; Tan Jin Sheng
      </Typography>
      <Box sx={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Row sign="" kind="open" label="Webbey Commission" sub="Company margin recorded on the sale" value={handlePriceWithRM(webbeyActual)} pct={pctOfIn(webbeyActual)} />
        <Row sign="" kind="open" label="Tan Jin Sheng — total" sub="Commission + slips" value={handlePriceWithRM(tjsTotal)} pct={pctOfIn(tjsTotal)} />
        <Row sign="" kind="child" label="PSC" value={handlePriceWithRM(tjsPsc)} />
        <Row sign="" kind="child" label="DOC" value={handlePriceWithRM(tjsDoc)} />
        <Row sign="" kind="child" label="TOC" value={handlePriceWithRM(tjsToc)} />
        <Row sign="" kind="child" label="MOC" value={handlePriceWithRM(tjsMoc)} />
        <Row sign="" kind="child" label="Incentive + Extra slips" value={handlePriceWithRM(tjsSlips)} />
        <Row sign="=" kind="total" label="Webbey + Tan Jin Sheng" sub="Everything retained by the company and the founder" value={handlePriceWithRM(webbeyActual + tjsTotal)} pct={pctOfIn(webbeyActual + tjsTotal)} />
      </Box>

      {/* Reconciliation — the system's own margin figure, as a cross-check on Nett. */}
      <Box sx={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Row sign="" kind="open" label="Webbey Commission" sub="What the system recorded as margin" value={handlePriceWithRM(webbeyActual)} />
        <Row
          sign={Math.abs(webbeyDrift) < 1 ? '=' : '≠'}
          kind="subtotal"
          label={Math.abs(webbeyDrift) < 1 ? 'Tallies with Nett' : 'Difference vs Nett'}
          sub={Math.abs(webbeyDrift) < 1
            ? 'The two agree exactly'
            : 'Timing — a sale’s claim, PSC and Webbey share can settle in different months'}
          value={handlePriceWithRM(Math.abs(webbeyDrift))}
          color={Math.abs(webbeyDrift) < 1 ? '#059669' : '#B45309'}
        />
      </Box>

      {/* Cash out — commission plus the slips that sit outside the claim. */}
      <Box sx={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Row sign="−" label="Paid Out" sub="Commission to agents" value={handlePriceWithRM(paidOut)} pct={pctOfIn(paidOut)} />
        <Row sign="−" label="Incentive + Extra Slips" sub="Approved slips — outside the commission claim" value={handlePriceWithRM(slipsTotal)} pct={pctOfIn(slipsTotal)} />
        <Row sign="=" kind="total" label="Total Cash Out" sub="Everything paid in this period" value={handlePriceWithRM(totalOut)} pct={pctOfIn(totalOut)} />
      </Box>

      {/* Profit ratios */}
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: FONT, mb: 1 }}>
        Profitability
      </Typography>
      {/* Two ratios only. The TJS earnings/take cards and the "what if TJS earnings stayed
          with company" block were removed — TJS now appears as real rows inside the
          statement above, which answers the same question without hypotheticals.
          (user 2026-08-25) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5, mb: 3 }}>
        <StatCard label="Profit Margin" value={profitMarginPct} sub="Nett ÷ Claimed" borderColor="#1A8CFF" />
        <StatCard label="Commission Payout Ratio" value={commissionRatioPct} sub="Total Cash Out ÷ Claimed" borderColor="#EF4444" />
      </Box>

      {/* Monthly In vs Out */}
      <Card sx={{ borderRadius: '14px', mb: 3 }}>
        <CardContent>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Monthly Cashflow (In vs Out)</Typography>
          {monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyTrend} margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                <YAxis tickFormatter={v => formatCompactRm(v)} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                <Tooltip formatter={v => handlePriceWithRM(v)} />
                <Legend />
                <Bar dataKey="In" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Out" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data for selected period</Typography>
          )}
        </CardContent>
      </Card>

      {/* Agent breakdown */}
      <Card sx={{ borderRadius: '14px' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', fontFamily: FONT }}>
              Agent Breakdown
              <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: '#64748B', fontWeight: 400 }}>
                ({filteredAgents.length} of {agentBreakdown.length} agents)
              </Typography>
            </Typography>
            <TextField
              size="small"
              placeholder="Search agent…"
              value={agentSearch}
              onChange={(e) => setAgentSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                sx: { fontFamily: FONT, fontSize: '0.85rem', borderRadius: '8px' },
              }}
              sx={{ width: 220 }}
            />
          </Box>
          {filteredAgents.length > 0 ? (
            <>
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontFamily: FONT, fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #E2E8F0', backgroundColor: '#F8FAFC' } }}>
                      <TableCell>#</TableCell>
                      <TableCell>Agent</TableCell>
                      <TableCell align="right">PSC</TableCell>
                      <TableCell align="right">DOC</TableCell>
                      <TableCell align="right">TOC</TableCell>
                      <TableCell align="right">MOC</TableCell>
                      <TableCell align="right">Slips</TableCell>
                      <TableCell align="right">Total Payout</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAgents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((a, i) => {
                      const rowNum = page * rowsPerPage + i + 1;
                      return (
                        <TableRow key={a.paymentTo} hover
                          onClick={() => a.paymentTo && navigate(`/reports/commission/agent/${a.paymentTo}`)}
                          sx={{ cursor: 'pointer', '& td': { fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9' }, '&:hover': { backgroundColor: '#F0F7FF' } }}>
                          <TableCell sx={{ fontWeight: 600, color: '#64748B' }}>{rowNum}</TableCell>
                          <TableCell sx={{ fontWeight: 500, color: '#1A8CFF' }}>{a.name}</TableCell>
                          <TableCell align="right">{handlePriceWithRM(a.psc)}</TableCell>
                          <TableCell align="right">{handlePriceWithRM(a.doc)}</TableCell>
                          <TableCell align="right">{handlePriceWithRM(a.toc)}</TableCell>
                          <TableCell align="right">{handlePriceWithRM(a.moc)}</TableCell>
                          <TableCell align="right">{handlePriceWithRM(a.slips)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{handlePriceWithRM(a.total)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredAgents.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  borderTop: '1px solid #E2E8F0',
                  '& .MuiTablePagination-toolbar': {
                    fontFamily: FONT,
                    fontSize: '0.78rem',
                    minHeight: 44,
                    paddingLeft: 1,
                    paddingRight: 1,
                    flexWrap: 'wrap',
                    rowGap: 0.5,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    margin: 0,
                    fontFamily: FONT,
                    fontSize: '0.78rem',
                  },
                  '& .MuiTablePagination-spacer': { display: 'none' },
                  '& .MuiTablePagination-actions': { marginLeft: 'auto' },
                }}
              />
            </>
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No agents match the search</Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
